import { Injectable } from '@angular/core';
import { Params } from '@angular/router';

import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { EventFactory } from '@frontend/sports/grid/core/feature/factories';
import { GridActions, IGridRootState } from '@frontend/sports/grid/core/feature/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatMap, filter, map, of, switchMap, withLatestFrom } from 'rxjs';

import { SortingStateService } from '../event-list-shared/sport/services/sorting-state.service';
import { LiveEventListService } from './live-event-list.service';

@Injectable()
export class LiveGridEffects {
    private lastRouteParams: Params;
    private isLivePage = false;
    private readonly liveTag = [
        RouteTag.Live,
        RouteTag.LiveAll,
        RouteTag.LiveHighlights,
        RouteTag.LiveRegion,
        RouteTag.LiveSport,
        RouteTag.LiveVideo,
        RouteTag.LiveEsports,
    ];

    constructor(
        private liveEventListService: LiveEventListService,
        private eventFactory: EventFactory,
        private routerEventsService: RouterEventsService,
        private sortingStateService: SortingStateService,
        private actions$: Actions,
        private store: Store<IGridRootState>,
    ) {
        this.routerEventsService.currentActivationEnd.pipe(filter(isDefined)).subscribe((event) => {
            this.lastRouteParams = event.snapshot.params;

            this.isLivePage = this.liveTag.some((tag) => tag === event.snapshot.data.tag);
        });
    }

    onSortingChange$ = createEffect(() => {
        return this.actions$.pipe(
            filter(() => this.isLivePage),
            ofType(GridActions.changeSorting),
            concatMap((action) => of(action).pipe(withLatestFrom(this.store.pipe(map((state) => state.grid[action.payload.id]))))),
            switchMap(([action, grid]) => {
                this.sortingStateService.set('live', action.payload.sorting);

                const { regionId, tournamentId } = this.getRegionAndTournament(grid.sport.id);

                return this.liveEventListService
                    .getFixtures({
                        sportId: grid.sport.id,
                        regionId,
                        tournamentId,
                        sorting: grid.sorting,
                    })
                    .pipe(
                        switchMap((page) => this.eventFactory.create(page?.fixtures)),
                        concatMap((events) => {
                            if (events.length === 0) {
                                return [];
                            }
                            const grouping = this.liveEventListService.getGridGrouping(events, grid.sorting);

                            return [GridActions.setEvents({ payload: { id: action.payload.id, events, grouping } })];
                        }),
                    );
            }),
        );
    });

    private getRegionAndTournament(sportId: number): { regionId?: number; tournamentId?: number } {
        const { region } = this.lastRouteParams;
        const regionId = region && parseInt(region);

        if (sportId === SportConstant.Tennis) {
            return { tournamentId: regionId };
        } else {
            return { regionId };
        }
    }
}
